import { MainContent } from '@/components/content'
import { MainLayout } from '@/components/layouts'
import { Search } from '@/components/search'
import { useCanUseBenefits } from '@/hooks/useCanUseBenefits'
import { useMyBenefits } from '@/services/myBenefits'
import { claimableBenefits } from '@/pages/benefits/claimOptions'
import { ClubSearchFacet, applyFacets, availableFacets } from '../clubSearchFacets'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import { ClubItemLoading, MyClubItemLoading } from '@/components/skeleton'
import { eventTracker } from '@/config/trackingFunctions'
import { OFFSET, SCROLLING_HEIGHT_ABS_ERROR, SCROLLING_THROTTLING_DURATION } from '@/constants/Common'
import { ROUTE_MY_CLUBS } from '@/constants/Routes'
import { useDebounce } from '@/hooks/useDebounce'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { useUserDetails } from '@/hooks/useUserDetails'
import { authSelector } from '@/pages/auth/selectors'
import { appSelector } from '@/pages/home/selectors'
import { userSelector } from '@/pages/user/selectors'
import { throttle } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useMediaQuery } from 'usehooks-ts'
import { getClubs, getMyClubs } from '../clubSlice'
import { ClubItem, MyClubItem } from '../components'
import { clubSelector } from '../selectors'
import { TRACKING_TYPES, PAGE_NAMES } from '@/constants/TrackingConstants'

export const Club = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { isShowDropdown } = useAppSelector(appSelector)
  const { myClubs, resClubs, isLoadingMyClubs, isLoadingClubs } = useAppSelector(clubSelector)
  const { token } = useAppSelector(authSelector)
  const { user } = useAppSelector(userSelector)
  // Same two halves `useHasClubManagementAccess` reads: a token on its own can be a stale one whose
  // profile never loaded.
  const isAuthenticated = !!token && !!user

  const userData = useUserDetails()
  const mobileL = useMediaQuery('(max-width: 425px)')
  const mobileM = useMediaQuery('(max-width: 375px)')

  const [searchText, setSearchText] = useState('')
  // Facets narrow whatever the search returned; they are a separate axis from what is searched, so
  // they live in a registry rather than as one-off booleans here. Off by default: most people
  // browsing clubs are not mid-claim, and a filter that silently narrowed the list would read as
  // "there are no clubs near me".
  const [activeFacets, setActiveFacets] = useState<ClubSearchFacet[]>([])
  // Signed-in members only. This page is reachable without a session, and "what do I hold" is a
  // question an anonymous visitor cannot ask — it 401s, and React Query retries, so browsing clubs
  // logged out produced a run of failed requests for a facet that could never apply to them anyway.
  const benefitsEnabled = useCanUseBenefits()
  const { data: myBenefits } = useMyBenefits(isAuthenticated && benefitsEnabled)
  const facets = availableFacets({
    hasClaimableBenefit: claimableBenefits(myBenefits ?? []).length > 0,
    benefitsEnabled,
  })
  const [offset, setOffset] = useState(OFFSET)

  const debouncedSearchText = useDebounce<string>(searchText || '*', 200)
  const debouncedSearchEventText = useDebounce<string>(searchText || '*', 500)

  const handleScroll = throttle(() => {
    if (
      Math.abs(window.innerHeight + document.documentElement.scrollTop - document.documentElement.offsetHeight) >
      SCROLLING_HEIGHT_ABS_ERROR
    ) {
      return
    }

    setOffset((pre) => pre + 18)
  }, SCROLLING_THROTTLING_DURATION)

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
    setOffset(OFFSET)
  }

  const onGetMyClubs = () => {
    dispatch(getMyClubs())
  }

  const onGetClubs = () => {
    dispatch(
      getClubs(
        applyFacets(
          {
            limit: 18,
            offset: offset,
            query: debouncedSearchText ? debouncedSearchText : '*',
          },
          activeFacets
        )
      )
    )
    if (searchText && searchText.length > 2 && debouncedSearchEventText === searchText) {
      eventTracker(TRACKING_TYPES.SEARCH_PERFORMED, {
        'Page Name': PAGE_NAMES.BROWSE_CLUBS,
        Keyword: searchText,
        ...userData,
      })
    }
  }

  const onNavigateMyClub = () => {
    navigate(ROUTE_MY_CLUBS)
  }

  useEffect(() => {
    if (resClubs.hasMore) window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resClubs])

  useEffect(() => {
    onGetMyClubs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    onGetClubs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, debouncedSearchText, debouncedSearchEventText, activeFacets])

  const returnElement = () => {
    if (isLoadingMyClubs && mobileL && mobileM)
      return (
        <>
          <MyClubItemLoading />
          <MyClubItemLoading />
        </>
      )

    if (isLoadingMyClubs && mobileL && !mobileM)
      return (
        <>
          <MyClubItemLoading />
          <MyClubItemLoading />
          <MyClubItemLoading />
        </>
      )

    if (isLoadingMyClubs && !mobileL && !mobileM)
      return (
        <>
          <MyClubItemLoading />
          <MyClubItemLoading />
          <MyClubItemLoading />
          <MyClubItemLoading />
        </>
      )
    if (myClubs.length && mobileL && mobileM)
      return myClubs?.slice(0, 2).map((item) => <MyClubItem key={item.clubId} club={item} />)

    if (myClubs.length && mobileL && !mobileM)
      return myClubs?.slice(0, 3).map((item) => <MyClubItem key={item.clubId} club={item} />)

    if (myClubs.length && !mobileL && !mobileM)
      return myClubs?.slice(0, 4).map((item) => <MyClubItem key={item.clubId} club={item} />)

    return <></>
  }

  return (
    <>
      {!isShowDropdown && <MobileSidebar />}
      <MainLayout>
        <MainSidebar />
        <MainContent>
          <div className="flex flex-col mb-20 lg:mb-0 pb-8">
            <h1 className="text-2xl md:text-[32px] md:leading-10 font-bold text-white">{t('club.clubs')}</h1>

            <div className="flex flex-col sm:flex-row gap-y-4 sm:gap-x-4 mt-4 md:mt-3">
              <Search searchText={searchText} onChangeInput={onChangeInput} className="py-2 px-4 md:w-52 gap-x-2" />
              {facets.map((facet) => {
                const active = activeFacets.includes(facet.value)
                return (
                  <button
                    key={facet.value}
                    className={`rounded-full px-3 py-2 text-sm transition-colors ${
                      active ? 'bg-white text-gray-900' : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                    onClick={() => {
                      setOffset(OFFSET)
                      setActiveFacets((prev) =>
                        active ? prev.filter((f) => f !== facet.value) : [...prev, facet.value]
                      )
                    }}
                  >
                    {t(facet.labelKey)}
                  </button>
                )
              })}
            </div>

            {myClubs.length ? (
              <div className="mt-9">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">{t('club.myClubs')}</h3>
                  <button className="text-white font-semibold text-sm hover:text-opacity-70" onClick={onNavigateMyClub}>
                    {t('common.viewAll')}
                  </button>
                </div>
                <div className={`flex gap-x-2 mt-4 flex-wrap`}>{returnElement()}</div>
              </div>
            ) : (
              <></>
            )}

            <div className="mt-4">
              <h2 className="text-white font-semibold text-xl">{t('club.exploreClubs')}</h2>
              <div className="flex flex-col md:flex-row gap-y-2 md:flex-wrap md:gap-2 mt-3">
                {resClubs?.hits?.map((item) => <ClubItem key={item.clubId} club={item} />)}
                {isLoadingClubs && (
                  <>
                    <ClubItemLoading />
                    <ClubItemLoading />
                    <ClubItemLoading />
                    <ClubItemLoading />
                    <ClubItemLoading />
                    <ClubItemLoading />
                    <ClubItemLoading />
                    <ClubItemLoading />
                    <ClubItemLoading />
                  </>
                )}
              </div>
              {resClubs && !resClubs?.hasMore && (
                <div className="flex justify-center mt-3">
                  <span className="text-white font-semibold text-opacity-60">{t('common.noMoreResults')}</span>
                </div>
              )}
            </div>
          </div>
        </MainContent>
      </MainLayout>
    </>
  )
}
